import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { Channel } from 'pusher-js';
import {
  AgentStreamEvents,
  ManagerBeginEvent,
  AgentStreamFinishEvent,
  ManagerCompleteEvent,
  AgentErrorEvent,
  AgentEvents,
  AgentProcessingStartEvent,
  AgentProcessingCompleteEvent,
  AsyncToolProgressEvent,
  AsyncToolEvent,
  ToolStartEvent,
  ToolEndEvent,
  AgentStreamBeginEvent,
  UIEvents,
  WorkboxItemCreatedEvent,
  CanvasAssetCreatedEvent,
  CanvasAssetRemovedEvent,
  CanvasFormUpdateEvent,
  PhoneCallStartedEvent,
  PhoneCallEndedEvent,
  WebRTCCallRequestedEvent,
  SubThreadCreatedEvent,
  SubThreadUpdateEvent,
  MessageEditedEvent,
  ResponseArtifactCreatedEvent,
  threadChannel,
} from '@dreamai/types';
import { usePusher } from './PusherContext';
import { useNewThread } from './NewThreadContext';
import { PUSHER_ENV } from '../lib/pusherClient';

// Special key for new threads that don't have an ID yet
export const NEW_THREAD = 'NEW_THREAD';

// Type for event callbacks
type EventCallback = (threadId: string, data: any) => void;

// Type for async tool progress data
export interface AsyncToolProgress {
  tool_id: string;
  threadId: string;
  progress: number;
  status:
    | 'INITIATED'
    | 'STARTED'
    | 'COMPLETED'
    | 'ERROR'
    | 'ABORTED'
    | 'PRE_ERROR';
  name: string;
}

// State context type - values that change frequently
interface ThreadStateContextType {
  loadingThreads: Record<string, boolean>;
  abortingThreads: Record<string, boolean>;
  fileAgentActiveThreads: Record<string, boolean>;
  asyncTools: Record<string, AsyncToolProgress>;
}

// Actions context type - stable callbacks
interface ThreadActionsContextType {
  setThreadLoading: (threadId: string | null, loading: boolean) => void;
  setThreadAborting: (threadId: string | null, aborting: boolean) => void;
  setFileAgentActive: (threadId: string | null, active: boolean) => void;
  setAsyncTools: React.Dispatch<
    React.SetStateAction<Record<string, AsyncToolProgress>>
  >;
  registerThread: (threadId: string) => void;
  unregisterThread: (threadId: string) => void;
  // New methods for event callback registration
  registerManagerBeginCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterManagerBeginCallback: (callbackId: string) => void;
  registerAgentStreamBeginCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterAgentStreamBeginCallback: (callbackId: string) => void;
  registerAgentStreamFinishCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterAgentStreamFinishCallback: (callbackId: string) => void;
  registerManagerCompleteCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterManagerCompleteCallback: (callbackId: string) => void;
  registerAgentErrorCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterAgentErrorCallback: (callbackId: string) => void;
  registerAbortSucceededCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterAbortSucceededCallback: (callbackId: string) => void;
  // Individual agent event callbacks
  registerIndividualAgentStartCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterIndividualAgentStartCallback: (callbackId: string) => void;
  registerIndividualAgentFinishCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterIndividualAgentFinishCallback: (callbackId: string) => void;
  // Phone call event callbacks
  registerPhoneCallStartedCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterPhoneCallStartedCallback: (callbackId: string) => void;
  registerPhoneCallEndedCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterPhoneCallEndedCallback: (callbackId: string) => void;
  // WebRTC call event callbacks
  registerWebRTCCallRequestedCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterWebRTCCallRequestedCallback: (callbackId: string) => void;
  registerWebRTCCallDismissedCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterWebRTCCallDismissedCallback: (callbackId: string) => void;
  dismissWebRTCCall: (threadId: string) => void;
  registerWebRTCCallAcceptedCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterWebRTCCallAcceptedCallback: (callbackId: string) => void;
  acceptWebRTCCall: (threadId: string) => void;
  // Active WebRTC call state management
  setActiveWebRTCCall: (
    threadId: string,
    callData: {
      agentName?: string;
      message?: string;
      structuredCallId?: string;
    },
  ) => void;
  clearActiveWebRTCCall: (threadId: string) => void;
  getActiveWebRTCCall: (threadId: string) => {
    agentName?: string;
    message?: string;
    timestamp: number;
    structuredCallId?: string;
  } | null;
  // Token and Tool event callbacks
  registerTokenCallback: (callbackId: string, callback: EventCallback) => void;
  unregisterTokenCallback: (callbackId: string) => void;
  registerThinkingTokenCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterThinkingTokenCallback: (callbackId: string) => void;
  registerToolStartCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterToolStartCallback: (callbackId: string) => void;
  registerToolEndCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterToolEndCallback: (callbackId: string) => void;
  // Async tool event callbacks
  registerToolIsAsyncCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterToolIsAsyncCallback: (callbackId: string) => void;
  registerAsyncToolStartCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterAsyncToolStartCallback: (callbackId: string) => void;
  registerAsyncToolProgressCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterAsyncToolProgressCallback: (callbackId: string) => void;
  registerAsyncToolEndCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterAsyncToolEndCallback: (callbackId: string) => void;
  registerAsyncToolErrorCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterAsyncToolErrorCallback: (callbackId: string) => void;
  registerAsyncToolAbortCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterAsyncToolAbortCallback: (callbackId: string) => void;
  // Workbox item created event callbacks
  registerWorkboxItemCreatedCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterWorkboxItemCreatedCallback: (callbackId: string) => void;

  // Canvas asset created event callbacks
  registerCanvasAssetCreatedCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterCanvasAssetCreatedCallback: (callbackId: string) => void;

  // Canvas asset removed event callbacks
  registerCanvasAssetRemovedCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterCanvasAssetRemovedCallback: (callbackId: string) => void;

  // Canvas form update event callbacks
  registerCanvasFormUpdateCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterCanvasFormUpdateCallback: (callbackId: string) => void;

  // Canvas component updated event callbacks
  registerCanvasComponentUpdatedCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterCanvasComponentUpdatedCallback: (callbackId: string) => void;

  // Confetti event callbacks
  registerConfettiCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterConfettiCallback: (callbackId: string) => void;

  // Sub-thread event callbacks
  registerSubThreadCreatedCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterSubThreadCreatedCallback: (callbackId: string) => void;
  registerSubThreadUpdateCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterSubThreadUpdateCallback: (callbackId: string) => void;

  // Message edited event callbacks
  registerMessageEditedCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterMessageEditedCallback: (callbackId: string) => void;

  // Response artifact created event callbacks
  registerResponseArtifactCreatedCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterResponseArtifactCreatedCallback: (callbackId: string) => void;

  // Response artifact updated event callbacks
  registerResponseArtifactUpdatedCallback: (
    callbackId: string,
    callback: EventCallback,
  ) => void;
  unregisterResponseArtifactUpdatedCallback: (callbackId: string) => void;
}

// Combined type for backward compatibility
type ThreadLoadingContextType = ThreadStateContextType &
  ThreadActionsContextType;

// Separate contexts for state and actions
const ThreadStateContext = createContext<ThreadStateContextType | undefined>(
  undefined,
);
const ThreadActionsContext = createContext<
  ThreadActionsContextType | undefined
>(undefined);

// Legacy context for backward compatibility (will be removed eventually)
const ThreadLoadingContext = createContext<
  ThreadLoadingContextType | undefined
>(undefined);

interface ThreadLoadingProviderProps {
  children: ReactNode;
}

export const ThreadLoadingProvider: React.FC<ThreadLoadingProviderProps> = ({
  children,
}) => {
  // Get thread ID and new flag from URL params
  const [searchParams] = useSearchParams();
  const urlThreadId = searchParams.get('thread_id');

  // Get new thread ID from context
  const { newThreadId } = useNewThread();

  // Refs for token and tool callbacks
  const tokenCallbacksRef = useRef<Record<string, EventCallback>>({});
  const thinkingTokenCallbacksRef = useRef<Record<string, EventCallback>>({});
  const toolStartCallbacksRef = useRef<Record<string, EventCallback>>({});
  const toolEndCallbacksRef = useRef<Record<string, EventCallback>>({});

  // Refs for individual agent callbacks
  const individualAgentStartCallbacksRef = useRef<
    Record<string, EventCallback>
  >({});
  const individualAgentFinishCallbacksRef = useRef<
    Record<string, EventCallback>
  >({});

  // Refs for phone call callbacks
  const phoneCallStartedCallbacksRef = useRef<Record<string, EventCallback>>(
    {},
  );
  const phoneCallEndedCallbacksRef = useRef<Record<string, EventCallback>>({});

  // Refs for WebRTC call callbacks
  const webrtcCallRequestedCallbacksRef = useRef<Record<string, EventCallback>>(
    {},
  );
  const webrtcCallDismissedCallbacksRef = useRef<Record<string, EventCallback>>(
    {},
  );
  const webrtcCallAcceptedCallbacksRef = useRef<Record<string, EventCallback>>(
    {},
  );

  // Store active WebRTC calls per thread
  const [activeWebRTCCalls, setActiveWebRTCCalls] = useState<
    Record<
      string,
      {
        agentName?: string;
        message?: string;
        timestamp: number;
        structuredCallId?: string;
      }
    >
  >({});

  // Create a ref to store the previous URL state
  const prevUrlRef = useRef<string | null>(null);

  // Track URL changes and reset NEW_THREAD states when navigating to a new thread page
  useEffect(() => {
    console.log('searchParams changed effect running', Date.now());

    // Get the current URL and serialize searchParams for comparison
    const currentUrl = window.location.href;
    const searchParamsString = JSON.stringify(
      Object.fromEntries(searchParams.entries()),
    );

    // Check if we're on the new thread page
    const hasLoadedNewThreadPage =
      searchParams.get('new') === '' || searchParams.get('new') === 'true';

    console.log(
      'Current URL params:',
      searchParamsString,
      'hasLoadedNewThreadPage:',
      hasLoadedNewThreadPage,
      'currentUrl:',
      currentUrl,
    );

    // Only reset states when we're on a new thread page WITHOUT a thread_id
    if (hasLoadedNewThreadPage && !urlThreadId) {
      console.log(
        'New thread page without thread_id detected, resetting all states',
      );

      // Reset loading, aborting, and UI agent active states for NEW_THREAD
      setLoadingThreads((prev) => {
        console.log('Setting loading threads', prev, 'to', {
          ...prev,
          [NEW_THREAD]: false,
        });
        return {
          ...prev,
          [NEW_THREAD]: false,
        };
      });

      setAbortingThreads((prev) => {
        console.log('Setting aborting threads', prev, 'to', {
          ...prev,
          [NEW_THREAD]: false,
        });
        return {
          ...prev,
          [NEW_THREAD]: false,
        };
      });

      setFileAgentActiveThreads((prev) => {
        const updated = { ...prev };
        delete updated[NEW_THREAD];
        console.log('Setting file agent active threads', prev, 'to', updated);
        return updated;
      });

      console.log(
        'ThreadLoadingContext: Reset all states for NEW_THREAD due to &new in URL',
      );
    }
  }, [searchParams]); // Only run when searchParams change

  // Clear NEW_THREAD loading state when a new thread ID is received
  useEffect(() => {
    if (newThreadId) {
      // When we have a new thread ID, clear the loading state for NEW_THREAD
      // as this means the last new thread has been submitted and become a real thread
      setLoadingThreads((prev) => ({
        ...prev,
        [NEW_THREAD]: false,
      }));
      console.log('ThreadLoadingContext: Cleared loading state for NEW_THREAD');
    }
  }, [newThreadId]);

  // Maps to track loading, aborting, and file agent activity states by thread ID
  const [loadingThreads, setLoadingThreads] = useState<Record<string, boolean>>(
    {},
  );
  const [abortingThreads, setAbortingThreads] = useState<
    Record<string, boolean>
  >({});
  const [fileAgentActiveThreads, setFileAgentActiveThreads] = useState<
    Record<string, boolean>
  >({});

  // Map to track async tools by tool_id
  const [asyncTools, setAsyncTools] = useState<
    Record<string, AsyncToolProgress>
  >({});

  // Ref to track asyncTools for stable callbacks
  const asyncToolsRef = useRef<Record<string, AsyncToolProgress>>({});
  useEffect(() => {
    asyncToolsRef.current = asyncTools;
  }, [asyncTools]);

  // Track registered threads for Pusher subscriptions
  const [registeredThreads, setRegisteredThreads] = useState<Set<string>>(
    new Set(),
  );

  // Store references to Pusher channels
  const threadChannelsRef = useRef<Record<string, Channel>>({});

  // Store event callbacks
  const managerBeginCallbacksRef = useRef<Record<string, EventCallback>>({});
  const agentStreamBeginCallbacksRef = useRef<Record<string, EventCallback>>(
    {},
  );
  const agentStreamFinishCallbacksRef = useRef<Record<string, EventCallback>>(
    {},
  );
  const managerCompleteCallbacksRef = useRef<Record<string, EventCallback>>({});
  const agentErrorCallbacksRef = useRef<Record<string, EventCallback>>({});
  const abortSucceededCallbacksRef = useRef<Record<string, EventCallback>>({});
  const workboxItemCreatedCallbacksRef = useRef<Record<string, EventCallback>>(
    {},
  );

  // Refs for canvas asset created callbacks
  const canvasAssetCreatedCallbacksRef = useRef<Record<string, EventCallback>>(
    {},
  );

  // Refs for canvas asset removed callbacks
  const canvasAssetRemovedCallbacksRef = useRef<Record<string, EventCallback>>(
    {},
  );

  // Refs for canvas form update callbacks
  const canvasFormUpdateCallbacksRef = useRef<Record<string, EventCallback>>(
    {},
  );

  // Refs for canvas component updated callbacks
  const canvasComponentUpdatedCallbacksRef = useRef<
    Record<string, EventCallback>
  >({});

  // Refs for confetti callbacks
  const confettiCallbacksRef = useRef<Record<string, EventCallback>>({});

  // Store async tool event callbacks
  const toolIsAsyncCallbacksRef = useRef<Record<string, EventCallback>>({});
  const asyncToolStartCallbacksRef = useRef<Record<string, EventCallback>>({});
  const asyncToolProgressCallbacksRef = useRef<Record<string, EventCallback>>(
    {},
  );
  const asyncToolEndCallbacksRef = useRef<Record<string, EventCallback>>({});
  const asyncToolErrorCallbacksRef = useRef<Record<string, EventCallback>>({});

  // Store sub-thread event callbacks
  const subThreadCreatedCallbacksRef = useRef<Record<string, EventCallback>>(
    {},
  );
  const subThreadUpdateCallbacksRef = useRef<Record<string, EventCallback>>({});

  // Store message edited event callbacks
  const messageEditedCallbacksRef = useRef<Record<string, EventCallback>>({});

  // Store response artifact created event callbacks
  const responseArtifactCreatedCallbacksRef = useRef<
    Record<string, EventCallback>
  >({});

  // Store response artifact updated event callbacks
  const responseArtifactUpdatedCallbacksRef = useRef<
    Record<string, EventCallback>
  >({});

  // Refs for event handlers to avoid useEffect dependency changes
  const handlersRef = useRef<{
    handleManagerBegin: (threadId: string, data: ManagerBeginEvent) => void;
    handleAgentStreamBegin: (
      threadId: string,
      data: AgentStreamBeginEvent,
    ) => void;
    handleAgentStreamFinish: (
      threadId: string,
      data: AgentStreamFinishEvent,
    ) => void;
    handleManagerComplete: (
      threadId: string,
      data: ManagerCompleteEvent,
    ) => void;
    handleAgentError: (threadId: string, data: AgentErrorEvent) => void;
    handleAbortSucceeded: (threadId: string, data: any) => void;
    handleIndividualAgentStart: (
      threadId: string,
      data: AgentProcessingStartEvent,
    ) => void;
    handleIndividualAgentFinish: (
      threadId: string,
      data: AgentProcessingCompleteEvent,
    ) => void;
    handleToolIsAsync: (threadId: string, data: AsyncToolEvent) => void;
    handleAsyncToolStart: (threadId: string, data: AsyncToolEvent) => void;
    handleAsyncToolProgress: (
      threadId: string,
      data: AsyncToolProgressEvent,
    ) => void;
    handleAsyncToolEnd: (threadId: string, data: AsyncToolEvent) => void;
    handleAsyncToolError: (threadId: string, data: AsyncToolEvent) => void;
    handlePhoneCallStarted: (
      threadId: string,
      data: PhoneCallStartedEvent,
    ) => void;
    handlePhoneCallEnded: (threadId: string, data: PhoneCallEndedEvent) => void;
    handleWebRTCCallRequested: (
      threadId: string,
      data: WebRTCCallRequestedEvent,
    ) => void;
    handleToken: (threadId: string, data: any) => void;
    handleThinkingToken: (
      threadId: string,
      data: { thread_id: string; content: string },
    ) => void;
    handleToolStart: (threadId: string, data: ToolStartEvent) => void;
    handleToolEnd: (threadId: string, data: ToolEndEvent) => void;
    handleAsyncToolAbort: (threadId: string, data: AsyncToolEvent) => void;
    handleWorkboxItemCreated: (threadId: string, data: any) => void;
    handleCanvasAssetCreated: (threadId: string, data: any) => void;
    handleCanvasAssetRemoved: (threadId: string, data: any) => void;
    handleCanvasFormUpdate: (threadId: string, data: any) => void;
    handleCanvasComponentUpdated: (threadId: string, data: any) => void;
    handleConfetti: (threadId: string, data: any) => void;
    handleSubThreadCreated: (
      threadId: string,
      data: SubThreadCreatedEvent,
    ) => void;
    handleSubThreadUpdate: (
      threadId: string,
      data: SubThreadUpdateEvent,
    ) => void;
    handleMessageEdited: (threadId: string, data: MessageEditedEvent) => void;
    handleResponseArtifactCreated: (
      threadId: string,
      data: ResponseArtifactCreatedEvent,
    ) => void;
    handleResponseArtifactUpdated: (threadId: string, data: any) => void;
  } | null>(null);

  // Get Pusher context
  const pusherContext = usePusher(true);

  // Helper function to check if a thread has any active async tools in a given tools state
  // This is the core implementation that can be used both memoized and unmemoized
  const checkForActiveAsyncTools = (
    tools: Record<string, AsyncToolProgress>,
    threadId: string,
  ): boolean => {
    return Object.values(tools).some(
      (tool) =>
        tool.threadId === threadId &&
        tool.status !== 'ERROR' &&
        tool.status !== 'PRE_ERROR' &&
        tool.status !== 'ABORTED' &&
        tool.status !== 'COMPLETED',
    );
  };

  // Memoized version that uses the asyncTools ref for stable callback
  // This is used in most places where we need to check for active tools
  const hasActiveAsyncTools = useCallback((threadId: string): boolean => {
    return checkForActiveAsyncTools(asyncToolsRef.current, threadId);
  }, []);

  // Set loading state for a thread, with special handling for setting to false
  const setThreadLoading = useCallback(
    (threadId: string | null, loading: boolean, skipAsyncToolCheck = false) => {
      // If threadId is null, use the special NEW_THREAD key
      const key = threadId || NEW_THREAD;

      // If trying to set loading to false, check if there are any active async tools
      // skipAsyncToolCheck allows bypassing this check in cases where we've already
      // verified there are no active tools or we explicitly want to override this behavior
      // (e.g., when handling an abort event where we know the tool status is being updated)
      if (
        !loading &&
        threadId &&
        !skipAsyncToolCheck &&
        hasActiveAsyncTools(threadId)
      ) {
        console.log(
          `ThreadLoadingContext: Not setting thread ${threadId} to non-loading state because it has active async tools`,
        );
        return; // Don't set loading to false if there are active async tools
      }

      setLoadingThreads((prev) => ({
        ...prev,
        [key]: loading,
      }));
    },
    [],
  );

  // Set aborting state for a thread
  const setThreadAborting = useCallback(
    (threadId: string | null, aborting: boolean) => {
      // If threadId is null, use the special NEW_THREAD key
      const key = threadId || NEW_THREAD;
      setAbortingThreads((prev) => ({
        ...prev,
        [key]: aborting,
      }));
    },
    [],
  );

  // Set file agent active state for a thread
  const setFileAgentActive = useCallback(
    (threadId: string | null, active: boolean) => {
      // If threadId is null, use the special NEW_THREAD key
      const key = threadId || NEW_THREAD;
      setFileAgentActiveThreads((prev: Record<string, boolean>) => ({
        ...prev,
        [key]: active,
      }));
    },
    [],
  );

  // Register a thread for monitoring
  const registerThread = useCallback((threadId: string) => {
    if (!threadId) return;

    setRegisteredThreads((prev) => {
      const newSet = new Set(prev);
      newSet.add(threadId);
      return newSet;
    });
  }, []);

  // Unregister a thread from monitoring
  const unregisterThread = useCallback((threadId: string) => {
    if (!threadId) return;

    setRegisteredThreads((prev) => {
      const newSet = new Set(prev);
      newSet.delete(threadId);
      return newSet;
    });
  }, []);

  // Handler for agent stream begin events
  const handleAgentStreamBegin = useCallback(
    (threadId: string, data: AgentStreamBeginEvent) => {
      console.log(
        `ThreadLoadingContext: Agent stream begin for thread ${threadId}`,
      );

      // Set the thread as loading - agent is actively streaming, so it's processing
      // This also handles recovery from errors where AGENT_ERROR set loading to false
      // but the agent continued processing internally
      setThreadLoading(threadId, true);

      // Call all registered callbacks
      Object.values(agentStreamBeginCallbacksRef.current).forEach(
        (callback) => {
          try {
            callback(threadId, data);
          } catch (error) {
            console.error(
              'ThreadLoadingContext: Error in agent stream begin callback:',
              error,
            );
          }
        },
      );
    },
    [setThreadLoading],
  );

  // Handler for manager begin events
  const handleManagerBegin = useCallback(
    (threadId: string, data: ManagerBeginEvent) => {
      console.log(`ThreadLoadingContext: Agent begin for thread ${threadId}`);

      // Set the thread as loading
      setThreadLoading(threadId, true);

      // Call all registered callbacks
      Object.values(managerBeginCallbacksRef.current).forEach((callback) => {
        try {
          callback(threadId, data);
        } catch (error) {
          console.error('Error in agent begin callback:', error);
        }
      });
    },
    [setThreadLoading],
  );

  // Handler for token events
  const handleToken = useCallback((threadId: string, data: any) => {
    // Call all registered callbacks
    Object.values(tokenCallbacksRef.current).forEach((callback) => {
      try {
        callback(threadId, data);
      } catch (error) {
        console.error('Error in token callback:', error);
      }
    });
  }, []);

  // Handler for tool start events
  const handleToolStart = useCallback(
    (threadId: string, data: ToolStartEvent) => {
      console.log(
        `ThreadLoadingContext: Tool start for thread ${data.thread_id}:`,
        data,
      );
      // Call all registered callbacks
      Object.values(toolStartCallbacksRef.current).forEach((callback) => {
        try {
          callback(data.thread_id, data);
        } catch (error) {
          console.error('Error in tool start callback:', error);
        }
      });
    },
    [],
  );

  // Handler for tool end events
  const handleToolEnd = useCallback((threadId: string, data: ToolEndEvent) => {
    console.log(
      `ThreadLoadingContext: Tool end for thread ${data.thread_id}:`,
      data,
    );
    // Call all registered callbacks
    Object.values(toolEndCallbacksRef.current).forEach((callback) => {
      try {
        callback(data.thread_id, data);
      } catch (error) {
        console.error('Error in tool end callback:', error);
      }
    });
  }, []);

  // Handler for thinking token events
  const handleThinkingToken = useCallback(
    (threadId: string, data: { thread_id: string; content: string }) => {
      // Call all registered callbacks
      Object.values(thinkingTokenCallbacksRef.current).forEach((callback) => {
        try {
          callback(data.thread_id, data);
        } catch (error) {
          console.error('Error in thinking token callback:', error);
        }
      });
    },
    [],
  );

  // Handler for agent stream finish events
  const handleAgentStreamFinish = useCallback(
    (threadId: string, data: AgentStreamFinishEvent) => {
      console.log(
        `ThreadLoadingContext: Agent finished for thread ${threadId}:`,
        data,
      );
      // Don't set loading to false yet, wait for AGENT_COMPLETE

      // Call all registered callbacks
      Object.values(agentStreamFinishCallbacksRef.current).forEach(
        (callback) => {
          try {
            callback(threadId, data);
          } catch (error) {
            console.error('Error in agent finish callback:', error);
          }
        },
      );
    },
    [],
  );

  // Handler for manager complete events
  const handleManagerComplete = useCallback(
    (threadId: string, data: ManagerCompleteEvent) => {
      console.log(
        `ThreadLoadingContext: Agent completed for thread ${threadId}:`,
        data,
      );

      // Update loading state
      setThreadLoading(threadId, false);

      // Call all registered callbacks
      Object.values(managerCompleteCallbacksRef.current).forEach((callback) => {
        try {
          callback(threadId, data);
        } catch (error) {
          console.error('Error in agent complete callback:', error);
        }
      });
    },
    [setThreadLoading],
  );

  // Handler for agent error events
  const handleAgentError = useCallback(
    (threadId: string, data: AgentErrorEvent) => {
      console.log(
        `ThreadLoadingContext: Agent error for thread ${threadId}:`,
        data,
      );

      // Update loading state
      setThreadLoading(threadId, false);
      setThreadAborting(threadId, false);
      setFileAgentActive(threadId, false);

      // Call all registered callbacks
      Object.values(agentErrorCallbacksRef.current).forEach((callback) => {
        try {
          callback(threadId, data);
        } catch (error) {
          console.error('Error in agent error callback:', error);
        }
      });
    },
    [setThreadLoading, setThreadAborting],
  );

  // Handler for abort succeeded events
  const handleAbortSucceeded = useCallback(
    (threadId: string, data: any) => {
      console.log(
        `ThreadLoadingContext: Abort succeeded for thread ${threadId}:`,
        data,
      );
      setThreadLoading(threadId, false);
      setThreadAborting(threadId, false);
      setFileAgentActive(threadId, false);

      // Call all registered callbacks
      Object.values(abortSucceededCallbacksRef.current).forEach((callback) => {
        try {
          callback(threadId, data);
        } catch (error) {
          console.error('Error in abort succeeded callback:', error);
        }
      });
    },
    [setThreadLoading, setThreadAborting],
  );

  // Handler for individual agent start events
  const handleIndividualAgentStart = useCallback(
    (threadId: string, data: AgentProcessingStartEvent) => {
      console.log(
        `ThreadLoadingContext: Individual agent started for thread ${threadId}: ${data.agentName}`,
        data,
      );
      if (data.agentName === 'UI Agent') {
        // UI Agent references removed
      } else if (data.agentName === 'File Agent') {
        setFileAgentActive(threadId, true);
      }

      // Call all registered callbacks
      Object.values(individualAgentStartCallbacksRef.current).forEach(
        (callback) => {
          try {
            callback(threadId, data);
          } catch (error) {
            console.error('Error in individual agent start callback:', error);
          }
        },
      );
    },
    [],
  );

  // Handler for individual agent finish events
  const handleIndividualAgentFinish = useCallback(
    (threadId: string, data: AgentProcessingCompleteEvent) => {
      console.log(
        `ThreadLoadingContext: Individual agent finished for thread ${threadId}: ${data.agentName}`,
        data,
      );
      if (data.agentName === 'UI Agent') {
        // UI Agent references removed
      } else if (data.agentName === 'File Agent') {
        setFileAgentActive(threadId, false);
      }

      // Call all registered callbacks
      Object.values(individualAgentFinishCallbacksRef.current).forEach(
        (callback) => {
          try {
            callback(threadId, data);
          } catch (error) {
            console.error('Error in individual agent finish callback:', error);
          }
        },
      );
    },
    [],
  );

  // Handler for phone call started events
  const handlePhoneCallStarted = useCallback(
    (threadId: string, data: PhoneCallStartedEvent) => {
      console.log(
        `ThreadLoadingContext: Phone call started for thread ${threadId}`,
        data,
      );

      // Call all registered callbacks
      Object.values(phoneCallStartedCallbacksRef.current).forEach(
        (callback) => {
          try {
            callback(threadId, data);
          } catch (error) {
            console.error('Error in phone call started callback:', error);
          }
        },
      );
    },
    [],
  );

  // Handler for WebRTC call requested events
  const handleWebRTCCallRequested = useCallback(
    (threadId: string, data: WebRTCCallRequestedEvent) => {
      console.log(
        `ThreadLoadingContext: WebRTC call requested for thread ${threadId}`,
        data,
      );

      // Set the active call in global state
      setActiveWebRTCCalls((prev) => ({
        ...prev,
        [threadId]: {
          agentName: data.agentName,
          message: data.message,
          timestamp: Date.now(),
          structuredCallId: data.structuredCallId,
        },
      }));

      // Call all registered callbacks
      Object.values(webrtcCallRequestedCallbacksRef.current).forEach(
        (callback) => {
          try {
            callback(threadId, data);
          } catch (error) {
            console.error('Error in WebRTC call requested callback:', error);
          }
        },
      );
    },
    [],
  );

  // Handler for WebRTC call dismissed events
  const handleWebRTCCallDismissed = useCallback((threadId: string) => {
    console.log(
      `ThreadLoadingContext: WebRTC call dismissed for thread ${threadId}`,
    );

    // Clear the active call from global state
    setActiveWebRTCCalls((prev) => {
      const updated = { ...prev };
      delete updated[threadId];
      return updated;
    });

    // Call all registered callbacks
    Object.values(webrtcCallDismissedCallbacksRef.current).forEach(
      (callback) => {
        try {
          callback(threadId, {}); // Pass empty object to match EventCallback signature
        } catch (error) {
          console.error('Error in WebRTC call dismissed callback:', error);
        }
      },
    );
  }, []);

  // Handler for WebRTC call accepted events
  const handleWebRTCCallAccepted = useCallback((threadId: string) => {
    console.log(
      `ThreadLoadingContext: WebRTC call accepted for thread ${threadId}`,
    );

    // Clear the active call from global state (accepting transitions to audio mode)
    setActiveWebRTCCalls((prev) => {
      const updated = { ...prev };
      delete updated[threadId];
      return updated;
    });

    // Call all registered callbacks
    Object.values(webrtcCallAcceptedCallbacksRef.current).forEach(
      (callback) => {
        try {
          callback(threadId, {}); // Pass empty object to match EventCallback signature
        } catch (error) {
          console.error('Error in WebRTC call accepted callback:', error);
        }
      },
    );
  }, []);

  // Handler for phone call ended events
  const handlePhoneCallEnded = useCallback(
    (threadId: string, data: PhoneCallEndedEvent) => {
      console.log(
        `ThreadLoadingContext: Phone call ended for thread ${threadId}`,
        data,
      );

      // Call all registered callbacks
      Object.values(phoneCallEndedCallbacksRef.current).forEach((callback) => {
        try {
          callback(threadId, data);
        } catch (error) {
          console.error('Error in phone call ended callback:', error);
        }
      });
    },
    [],
  );

  // Handler for tool is async events (before the tool actually starts running)
  const handleToolIsAsync = useCallback(
    (threadId: string, data: AsyncToolEvent) => {
      console.log(
        `ThreadLoadingContext: Tool is async for thread ${data.thread_id}: ${data.tool_id}`,
        data,
      );

      // Set thread loading to true
      setThreadLoading(data.thread_id, true);

      // Add tool to async tools map
      setAsyncTools((prev) => ({
        ...prev,
        [data.tool_id]: {
          tool_id: data.tool_id,
          threadId: data.thread_id,
          progress: 0,
          status: 'INITIATED',
          name: data.tool,
        },
      }));

      // Call all registered callbacks
      Object.values(toolIsAsyncCallbacksRef.current).forEach((callback) => {
        try {
          callback(data.thread_id, data);
        } catch (error) {
          console.error('Error in tool is async callback:', error);
        }
      });
    },
    [setThreadLoading],
  );

  // Handler for async tool start events
  const handleAsyncToolStart = useCallback(
    (threadId: string, data: AsyncToolEvent) => {
      console.log(
        `ThreadLoadingContext: Async tool started for thread ${data.thread_id}: ${data.tool_id}`,
        data,
      );

      // Update tool status in async tools map
      setAsyncTools((prev) => {
        // Only update if the tool exists
        if (prev[data.tool_id]) {
          return {
            ...prev,
            [data.tool_id]: {
              ...prev[data.tool_id],
              status: 'STARTED',
            },
          };
        }
        return prev;
      });

      // Call all registered callbacks
      Object.values(asyncToolStartCallbacksRef.current).forEach((callback) => {
        try {
          callback(data.thread_id, data);
        } catch (error) {
          console.error('Error in async tool start callback:', error);
        }
      });
    },
    [],
  );

  // Handler for async tool progress events
  const handleAsyncToolProgress = useCallback(
    (threadId: string, data: AsyncToolProgressEvent) => {
      console.log(
        `ThreadLoadingContext: Async tool progress for thread ${threadId}: ${data.tool_id} - ${data.progress}%`,
        data,
      );

      // Update tool progress in async tools map
      setAsyncTools((prev) => {
        // Only update if the tool exists
        if (prev[data.tool_id]) {
          return {
            ...prev,
            [data.tool_id]: {
              ...prev[data.tool_id],
              progress: data.progress,
            },
          };
        }
        return prev;
      });

      // Call all registered callbacks
      Object.values(asyncToolProgressCallbacksRef.current).forEach(
        (callback) => {
          try {
            callback(threadId, data);
          } catch (error) {
            console.error('Error in async tool progress callback:', error);
          }
        },
      );
    },
    [],
  );

  // Handler for async tool end events
  const handleAsyncToolEnd = useCallback(
    (threadId: string, data: AsyncToolEvent) => {
      console.log(
        `ThreadLoadingContext: Async tool ended for thread ${threadId}: ${data.tool_id}`,
        data,
      );

      // Remove tool from async tools map
      setAsyncTools((prev) => {
        const newState = { ...prev };
        delete newState[data.tool_id];
        return newState;
      });

      // Always try to set loading to false - the setThreadLoading function will
      // check if there are any remaining active async tools and only set to false
      // if there are none
      setThreadLoading(threadId, false);

      // Call all registered callbacks
      Object.values(asyncToolEndCallbacksRef.current).forEach((callback) => {
        try {
          callback(threadId, data);
        } catch (error) {
          console.error('Error in async tool end callback:', error);
        }
      });
    },
    [setThreadLoading],
  );

  // Handler for workbox item created events
  const handleWorkboxItemCreated = useCallback(
    (threadId: string, data: { workboxItemId: string }) => {
      console.log(
        `ThreadLoadingContext: Workbox item created for thread ${threadId}:`,
        data,
      );

      // Call all registered callbacks
      Object.values(workboxItemCreatedCallbacksRef.current).forEach(
        (callback) => {
          try {
            callback(threadId, data);
          } catch (error) {
            console.error('Error in workbox item created callback:', error);
          }
        },
      );
    },
    [],
  );

  const handleCanvasAssetCreated = useCallback(
    (threadId: string, data: { canvasAssetId: string; title: string }) => {
      console.log(
        `ThreadLoadingContext: Canvas asset created for thread ${threadId}:`,
        data,
      );
      console.log(
        `ThreadLoadingContext: Number of registered callbacks:`,
        Object.keys(canvasAssetCreatedCallbacksRef.current).length,
        Object.keys(canvasAssetCreatedCallbacksRef.current),
      );

      // Call all registered callbacks
      Object.values(canvasAssetCreatedCallbacksRef.current).forEach(
        (callback) => {
          try {
            callback(threadId, data);
          } catch (error) {
            console.error('Error in canvas asset created callback:', error);
          }
        },
      );
    },
    [],
  );

  const handleCanvasAssetRemoved = useCallback(
    (threadId: string, data: { canvasAssetId: string }) => {
      console.log(
        `ThreadLoadingContext: Canvas asset removed for thread ${threadId}:`,
        data,
      );

      // Call all registered callbacks
      Object.values(canvasAssetRemovedCallbacksRef.current).forEach(
        (callback) => {
          try {
            callback(threadId, data);
          } catch (error) {
            console.error('Error in canvas asset removed callback:', error);
          }
        },
      );
    },
    [],
  );

  const handleCanvasFormUpdate = useCallback((threadId: string, data: any) => {
    console.log(
      `ThreadLoadingContext: Canvas form update for thread ${threadId}:`,
      data,
    );

    // Call all registered callbacks
    Object.values(canvasFormUpdateCallbacksRef.current).forEach((callback) => {
      try {
        callback(threadId, data);
      } catch (error) {
        console.error('Error in canvas form update callback:', error);
      }
    });
  }, []);

  const handleCanvasComponentUpdated = useCallback(
    (threadId: string, data: any) => {
      console.log(
        `ThreadLoadingContext: Canvas component updated for thread ${threadId}:`,
        data,
      );

      // Call all registered callbacks
      Object.values(canvasComponentUpdatedCallbacksRef.current).forEach(
        (callback) => {
          try {
            callback(threadId, data);
          } catch (error) {
            console.error('Error in canvas component updated callback:', error);
          }
        },
      );
    },
    [],
  );

  const handleConfetti = useCallback((threadId: string, data: any) => {
    console.log(
      `ThreadLoadingContext: Confetti event for thread ${threadId}:`,
      data,
    );

    // Call all registered callbacks
    Object.values(confettiCallbacksRef.current).forEach((callback) => {
      try {
        callback(threadId, data);
      } catch (error) {
        console.error('Error in confetti callback:', error);
      }
    });
  }, []);

  // Handler for async tool error events
  const handleAsyncToolError = useCallback(
    (threadId: string, data: AsyncToolEvent) => {
      console.log(
        `ThreadLoadingContext: Async tool error for thread ${threadId}: ${data.tool_id}`,
        data,
      );

      // Update tool status to PRE_ERROR
      setAsyncTools((prev) => {
        // Only update if the tool exists
        if (prev[data.tool_id]) {
          return {
            ...prev,
            [data.tool_id]: {
              ...prev[data.tool_id],
              status: 'PRE_ERROR',
            },
          };
        }
        return prev;
      });

      // Set a timeout to change status to ERROR after 2 seconds
      setTimeout(() => {
        setAsyncTools((prev) => {
          // Only update if the tool exists and is still in PRE_ERROR state
          if (prev[data.tool_id] && prev[data.tool_id].status === 'PRE_ERROR') {
            return {
              ...prev,
              [data.tool_id]: {
                ...prev[data.tool_id],
                status: 'ERROR',
              },
            };
          }
          return prev;
        });
      }, 5000);

      // Call all registered callbacks
      Object.values(asyncToolErrorCallbacksRef.current).forEach((callback) => {
        try {
          callback(threadId, data);
        } catch (error) {
          console.error('Error in async tool error callback:', error);
        }
      });
    },
    [],
  );

  // Reference for async tool abort callbacks
  const asyncToolAbortCallbacksRef = useRef<Record<string, EventCallback>>({});

  // Handler for async tool abort events
  const handleAsyncToolAbort = useCallback(
    (threadId: string, data: AsyncToolEvent) => {
      console.log(
        `ThreadLoadingContext: Async tool aborted for thread ${threadId}: ${data.tool_id}`,
        data,
      );

      // Update the tool status to ABORTED
      setAsyncTools((prevTools) => {
        // Only update if the tool exists
        if (!prevTools[data.tool_id]) {
          return prevTools;
        }

        // Create updated tools state with this tool marked as ABORTED
        const updatedTool: AsyncToolProgress = {
          ...prevTools[data.tool_id],
          status: 'ABORTED' as const, // Use 'as const' to ensure correct typing
        };

        return {
          ...prevTools,
          [data.tool_id]: updatedTool,
        };
      });

      // Use a setTimeout to ensure this executes after the state update is applied
      setTimeout(() => {
        // Use the unmemoized checkForActiveAsyncTools function directly with the ref
        // This ensures we're using the most up-to-date state and not a stale memoized value
        const hasActiveTools = checkForActiveAsyncTools(
          asyncToolsRef.current,
          threadId,
        );

        // If no active tools remain, set loading to false and skip the async tool check
        // since we've already verified there are no active tools
        setThreadLoading(threadId, false, true);
      }, 0);

      // Call all registered callbacks
      Object.values(asyncToolAbortCallbacksRef.current).forEach((callback) => {
        try {
          callback(threadId, data);
        } catch (error) {
          console.error('Error in async tool abort callback:', error);
        }
      });
    },
    [setThreadLoading],
  );

  // Handler for sub-thread created events
  const handleSubThreadCreated = useCallback(
    (threadId: string, data: SubThreadCreatedEvent) => {
      // Call all registered callbacks
      Object.values(subThreadCreatedCallbacksRef.current).forEach(
        (callback) => {
          try {
            callback(threadId, data);
          } catch (error) {
            console.error('Error in sub-thread created callback:', error);
          }
        },
      );
    },
    [],
  );

  // Handler for sub-thread update events
  const handleSubThreadUpdate = useCallback(
    (threadId: string, data: SubThreadUpdateEvent) => {
      // Call all registered callbacks
      Object.values(subThreadUpdateCallbacksRef.current).forEach((callback) => {
        try {
          callback(threadId, data);
        } catch (error) {
          console.error('Error in sub-thread update callback:', error);
        }
      });
    },
    [],
  );

  // Handler for message edited events
  const handleMessageEdited = useCallback(
    (threadId: string, data: MessageEditedEvent) => {
      console.log(
        `ThreadLoadingContext: Message edited for thread ${threadId}`,
        data,
      );
      // Call all registered callbacks
      Object.values(messageEditedCallbacksRef.current).forEach((callback) => {
        try {
          callback(threadId, data);
        } catch (error) {
          console.error('Error in message edited callback:', error);
        }
      });
    },
    [],
  );

  // Handler for response artifact created events
  const handleResponseArtifactCreated = useCallback(
    (threadId: string, data: ResponseArtifactCreatedEvent) => {
      // Call all registered callbacks
      Object.values(responseArtifactCreatedCallbacksRef.current).forEach(
        (callback) => {
          try {
            callback(threadId, data);
          } catch (error) {
            console.error(
              'Error in response artifact created callback:',
              error,
            );
          }
        },
      );
    },
    [],
  );

  // Handler for response artifact updated events (e.g., dismissed/actioned)
  const handleResponseArtifactUpdated = useCallback(
    (threadId: string, data: any) => {
      // Call all registered callbacks
      Object.values(responseArtifactUpdatedCallbacksRef.current).forEach(
        (callback) => {
          try {
            callback(threadId, data);
          } catch (error) {
            console.error(
              'Error in response artifact updated callback:',
              error,
            );
          }
        },
      );
    },
    [],
  );

  // Auto-register threads from URL and NewThreadContext
  useEffect(() => {
    // Register URL thread ID if available
    if (urlThreadId) {
      console.log(
        `ThreadLoadingContext: Auto-registering URL thread: ${urlThreadId}`,
      );
      registerThread(urlThreadId);
    }

    // Register new thread ID if available
    if (newThreadId) {
      console.log(
        `ThreadLoadingContext: Auto-registering new thread: ${newThreadId}`,
      );
      registerThread(newThreadId);
    }
  }, [urlThreadId, newThreadId, registerThread]);

  // Keep handlersRef in sync with current handlers
  useEffect(() => {
    handlersRef.current = {
      handleManagerBegin,
      handleAgentStreamBegin,
      handleAgentStreamFinish,
      handleManagerComplete,
      handleAgentError,
      handleAbortSucceeded,
      handleIndividualAgentStart,
      handleIndividualAgentFinish,
      handleToolIsAsync,
      handleAsyncToolStart,
      handleAsyncToolProgress,
      handleAsyncToolEnd,
      handleAsyncToolError,
      handlePhoneCallStarted,
      handlePhoneCallEnded,
      handleWebRTCCallRequested,
      handleToken,
      handleThinkingToken,
      handleToolStart,
      handleToolEnd,
      handleAsyncToolAbort,
      handleWorkboxItemCreated,
      handleCanvasAssetCreated,
      handleCanvasAssetRemoved,
      handleCanvasFormUpdate,
      handleCanvasComponentUpdated,
      handleConfetti,
      handleSubThreadCreated,
      handleSubThreadUpdate,
      handleMessageEdited,
      handleResponseArtifactCreated,
      handleResponseArtifactUpdated,
    };
  });

  // Set up Pusher subscriptions for registered threads
  useEffect(() => {
    if (!pusherContext || !pusherContext.pusherInstance) {
      return;
    }

    // Function to subscribe to a thread channel
    const subscribeToThread = (threadId: string) => {
      if (!threadId || threadChannelsRef.current[threadId]) return;

      try {
        // Create the thread-specific channel name
        const channelName = threadChannel(PUSHER_ENV, threadId);
        // Verbose logging disabled to reduce console spam
        // console.log(`ThreadLoadingContext: Subscribing to channel: ${channelName}`);

        // Subscribe to the channel
        const channel = pusherContext.pusherInstance.subscribe(channelName);
        threadChannelsRef.current[threadId] = channel;

        // Bind to agent events - use handlersRef to avoid dependency on handlers
        channel.bind(
          AgentStreamEvents.MANAGER_BEGIN,
          (data: ManagerBeginEvent) =>
            handlersRef.current?.handleManagerBegin(threadId, data),
        );
        channel.bind(
          AgentStreamEvents.AGENT_STREAM_BEGIN,
          (data: AgentStreamBeginEvent) =>
            handlersRef.current?.handleAgentStreamBegin(threadId, data),
        );
        channel.bind(
          AgentStreamEvents.AGENT_STREAM_FINISH,
          (data: AgentStreamFinishEvent) =>
            handlersRef.current?.handleAgentStreamFinish(threadId, data),
        );
        channel.bind(
          AgentStreamEvents.MANAGER_COMPLETE,
          (data: ManagerCompleteEvent) =>
            handlersRef.current?.handleManagerComplete(threadId, data),
        );

        channel.bind(AgentStreamEvents.AGENT_ERROR, (data: AgentErrorEvent) =>
          handlersRef.current?.handleAgentError(threadId, data),
        );

        channel.bind(AgentStreamEvents.AGENT_ABORT_SUCCEEDED, (data: any) =>
          handlersRef.current?.handleAbortSucceeded(threadId, data),
        );

        channel.bind(
          AgentEvents.INDIVIDUAL_AGENT_START,
          (data: AgentProcessingStartEvent) =>
            handlersRef.current?.handleIndividualAgentStart(threadId, data),
        );

        channel.bind(
          AgentEvents.INDIVIDUAL_AGENT_FINISH,
          (data: AgentProcessingCompleteEvent) =>
            handlersRef.current?.handleIndividualAgentFinish(threadId, data),
        );

        // Bind to phone call events
        channel.bind(
          AgentEvents.PHONE_CALL_STARTED,
          (data: PhoneCallStartedEvent) =>
            handlersRef.current?.handlePhoneCallStarted(threadId, data),
        );
        channel.bind(
          AgentEvents.WEBRTC_CALL_REQUESTED,
          (data: WebRTCCallRequestedEvent) =>
            handlersRef.current?.handleWebRTCCallRequested(threadId, data),
        );

        channel.bind(
          AgentEvents.PHONE_CALL_ENDED,
          (data: PhoneCallEndedEvent) =>
            handlersRef.current?.handlePhoneCallEnded(threadId, data),
        );

        // Bind to workbox-item-created events
        channel.bind(
          UIEvents.WORKBOX_ITEM_CREATED,
          (data: WorkboxItemCreatedEvent) =>
            handlersRef.current?.handleWorkboxItemCreated(threadId, data),
        );

        // Bind to canvas-asset-created events
        channel.bind(
          UIEvents.CANVAS_ASSET_CREATED,
          (data: CanvasAssetCreatedEvent) =>
            handlersRef.current?.handleCanvasAssetCreated(threadId, data),
        );

        // Bind to canvas-asset-removed events
        channel.bind(
          UIEvents.CANVAS_ASSET_REMOVED,
          (data: CanvasAssetRemovedEvent) =>
            handlersRef.current?.handleCanvasAssetRemoved(threadId, data),
        );

        // Bind to canvas-form-update events
        channel.bind(
          UIEvents.CANVAS_FORM_UPDATE,
          (data: CanvasFormUpdateEvent) =>
            handlersRef.current?.handleCanvasFormUpdate(threadId, data),
        );

        // Bind to canvas-component-updated events
        channel.bind(UIEvents.CANVAS_COMPONENT_UPDATED, (data: any) =>
          handlersRef.current?.handleCanvasComponentUpdated(threadId, data),
        );

        // Bind to confetti events
        channel.bind(UIEvents.CONFETTI, (data: any) =>
          handlersRef.current?.handleConfetti(threadId, data),
        );

        // Bind to async tool events
        channel.bind(AgentStreamEvents.TOOL_IS_ASYNC, (data: AsyncToolEvent) =>
          handlersRef.current?.handleToolIsAsync(threadId, data),
        );

        channel.bind(
          AgentStreamEvents.ASYNC_TOOL_START,
          (data: AsyncToolEvent) =>
            handlersRef.current?.handleAsyncToolStart(threadId, data),
        );

        channel.bind(
          AgentStreamEvents.ASYNC_TOOL_PROGRESS,
          (data: AsyncToolProgressEvent) =>
            handlersRef.current?.handleAsyncToolProgress(threadId, data),
        );

        channel.bind(AgentStreamEvents.ASYNC_TOOL_END, (data: AsyncToolEvent) =>
          handlersRef.current?.handleAsyncToolEnd(threadId, data),
        );

        channel.bind(
          AgentStreamEvents.ASYNC_TOOL_ERROR,
          (data: AsyncToolEvent) =>
            handlersRef.current?.handleAsyncToolError(threadId, data),
        );

        channel.bind(
          AgentStreamEvents.ASYNC_TOOL_ABORT,
          (data: AsyncToolEvent) =>
            handlersRef.current?.handleAsyncToolAbort(threadId, data),
        );

        // Bind to sub-thread events
        channel.bind(
          AgentStreamEvents.SUB_THREAD_CREATED,
          (data: SubThreadCreatedEvent) =>
            handlersRef.current?.handleSubThreadCreated(threadId, data),
        );
        channel.bind(
          AgentStreamEvents.SUB_THREAD_UPDATE,
          (data: SubThreadUpdateEvent) =>
            handlersRef.current?.handleSubThreadUpdate(threadId, data),
        );

        // Bind to token and tool events
        channel.bind(AgentStreamEvents.TOKEN, (data: any) =>
          handlersRef.current?.handleToken(threadId, data),
        );

        channel.bind(
          AgentStreamEvents.THINKING_TOKEN,
          (data: { thread_id: string; content: string }) =>
            handlersRef.current?.handleThinkingToken(threadId, data),
        );

        channel.bind(AgentStreamEvents.TOOL_START, (data: ToolStartEvent) =>
          handlersRef.current?.handleToolStart(threadId, data),
        );

        channel.bind(AgentStreamEvents.TOOL_END, (data: ToolEndEvent) =>
          handlersRef.current?.handleToolEnd(threadId, data),
        );

        // Bind to message edited events
        channel.bind(
          AgentStreamEvents.MESSAGE_EDITED,
          (data: MessageEditedEvent) =>
            handlersRef.current?.handleMessageEdited(threadId, data),
        );

        // Bind to response artifact created events
        channel.bind(
          AgentStreamEvents.RESPONSE_ARTIFACT_CREATED,
          (data: ResponseArtifactCreatedEvent) =>
            handlersRef.current?.handleResponseArtifactCreated(threadId, data),
        );

        // Bind to response artifact updated events
        channel.bind(AgentStreamEvents.RESPONSE_ARTIFACT_UPDATED, (data: any) =>
          handlersRef.current?.handleResponseArtifactUpdated(threadId, data),
        );

        // Verbose logging disabled
        // console.log(`ThreadLoadingContext: Successfully set up event handlers for thread: ${threadId}`);
      } catch (error) {
        console.error(
          `ThreadLoadingContext: Error setting up subscription for thread ${threadId}:`,
          error,
        );
      }
    };

    // Function to unsubscribe from a thread channel
    const unsubscribeFromThread = (threadId: string) => {
      // Verbose logging disabled
      // console.log('Unsubscribing from thread ' + threadId);
      if (!threadId || !threadChannelsRef.current[threadId]) return;

      try {
        const channel = threadChannelsRef.current[threadId];

        // Unbind our event handlers
        channel.unbind(AgentStreamEvents.MANAGER_BEGIN);
        channel.unbind(AgentStreamEvents.AGENT_STREAM_BEGIN);
        channel.unbind(AgentStreamEvents.AGENT_STREAM_FINISH);
        channel.unbind(AgentStreamEvents.MANAGER_COMPLETE);
        channel.unbind(AgentStreamEvents.AGENT_ERROR);
        channel.unbind(AgentStreamEvents.AGENT_ABORT_SUCCEEDED);
        channel.unbind(AgentEvents.INDIVIDUAL_AGENT_START);
        channel.unbind(AgentEvents.INDIVIDUAL_AGENT_FINISH);
        channel.unbind(AgentStreamEvents.TOOL_IS_ASYNC);
        channel.unbind(AgentStreamEvents.ASYNC_TOOL_START);
        channel.unbind(AgentStreamEvents.ASYNC_TOOL_PROGRESS);
        channel.unbind(AgentStreamEvents.ASYNC_TOOL_END);
        channel.unbind(AgentStreamEvents.ASYNC_TOOL_ERROR);
        channel.unbind(AgentStreamEvents.ASYNC_TOOL_ABORT);

        // Unbind sub-thread events
        channel.unbind(AgentStreamEvents.SUB_THREAD_CREATED);
        channel.unbind(AgentStreamEvents.SUB_THREAD_UPDATE);

        // Unbind the TOKEN, THINKING_TOKEN, TOOL_START, and TOOL_END events
        channel.unbind(AgentStreamEvents.TOKEN);
        channel.unbind(AgentStreamEvents.THINKING_TOKEN);
        channel.unbind(AgentStreamEvents.TOOL_START);
        channel.unbind(AgentStreamEvents.TOOL_END);

        // Unbind message edited event
        channel.unbind(AgentStreamEvents.MESSAGE_EDITED);

        // Unbind response artifact created event
        channel.unbind(AgentStreamEvents.RESPONSE_ARTIFACT_CREATED);

        // Unbind response artifact updated event
        channel.unbind(AgentStreamEvents.RESPONSE_ARTIFACT_UPDATED);

        // Don't actually unsubscribe from the channel to allow other components to use it
        // Just remove our reference
        delete threadChannelsRef.current[threadId];

        // Verbose logging disabled
        // console.log(`ThreadLoadingContext: Unbound event handlers for thread: ${threadId}`);
      } catch (error) {
        console.error(
          `ThreadLoadingContext: Error cleaning up subscription for thread ${threadId}:`,
          error,
        );
      }
    };

    // Subscribe to all registered threads
    registeredThreads.forEach(subscribeToThread);

    // Cleanup function
    return () => {
      // Unsubscribe from all thread channels
      Object.keys(threadChannelsRef.current).forEach(unsubscribeFromThread);
    };
  }, [registeredThreads, pusherContext]);

  // Methods for registering and unregistering callbacks
  const registerManagerBeginCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      managerBeginCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterManagerBeginCallback = useCallback((callbackId: string) => {
    delete managerBeginCallbacksRef.current[callbackId];
  }, []);

  const registerAgentStreamBeginCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      agentStreamBeginCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterAgentStreamBeginCallback = useCallback(
    (callbackId: string) => {
      delete agentStreamBeginCallbacksRef.current[callbackId];
    },
    [],
  );

  const registerAgentStreamFinishCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      agentStreamFinishCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterAgentStreamFinishCallback = useCallback(
    (callbackId: string) => {
      delete agentStreamFinishCallbacksRef.current[callbackId];
    },
    [],
  );

  const registerManagerCompleteCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      managerCompleteCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterManagerCompleteCallback = useCallback(
    (callbackId: string) => {
      delete managerCompleteCallbacksRef.current[callbackId];
    },
    [],
  );

  const registerAgentErrorCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      agentErrorCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterAgentErrorCallback = useCallback((callbackId: string) => {
    delete agentErrorCallbacksRef.current[callbackId];
  }, []);

  // Methods for registering and unregistering abort succeeded callbacks
  const registerAbortSucceededCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      abortSucceededCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterAbortSucceededCallback = useCallback((callbackId: string) => {
    delete abortSucceededCallbacksRef.current[callbackId];
  }, []);

  // Methods for registering and unregistering async tool callbacks
  const registerToolIsAsyncCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      toolIsAsyncCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterToolIsAsyncCallback = useCallback((callbackId: string) => {
    delete toolIsAsyncCallbacksRef.current[callbackId];
  }, []);

  const registerAsyncToolStartCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      asyncToolStartCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterAsyncToolStartCallback = useCallback((callbackId: string) => {
    delete asyncToolStartCallbacksRef.current[callbackId];
  }, []);

  const registerAsyncToolProgressCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      asyncToolProgressCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterAsyncToolProgressCallback = useCallback(
    (callbackId: string) => {
      delete asyncToolProgressCallbacksRef.current[callbackId];
    },
    [],
  );

  const registerAsyncToolEndCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      asyncToolEndCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterAsyncToolEndCallback = useCallback((callbackId: string) => {
    delete asyncToolEndCallbacksRef.current[callbackId];
  }, []);

  // Methods for registering and unregistering async tool error callbacks
  const registerAsyncToolErrorCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      asyncToolErrorCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterAsyncToolErrorCallback = useCallback((callbackId: string) => {
    delete asyncToolErrorCallbacksRef.current[callbackId];
  }, []);

  const registerAsyncToolAbortCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      asyncToolAbortCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterAsyncToolAbortCallback = useCallback((callbackId: string) => {
    delete asyncToolAbortCallbacksRef.current[callbackId];
  }, []);

  // Methods for registering and unregistering workbox item created callbacks
  const registerWorkboxItemCreatedCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      workboxItemCreatedCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterWorkboxItemCreatedCallback = useCallback(
    (callbackId: string) => {
      delete workboxItemCreatedCallbacksRef.current[callbackId];
    },
    [],
  );

  const registerCanvasAssetCreatedCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      canvasAssetCreatedCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterCanvasAssetCreatedCallback = useCallback(
    (callbackId: string) => {
      delete canvasAssetCreatedCallbacksRef.current[callbackId];
    },
    [],
  );

  const registerCanvasAssetRemovedCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      canvasAssetRemovedCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterCanvasAssetRemovedCallback = useCallback(
    (callbackId: string) => {
      delete canvasAssetRemovedCallbacksRef.current[callbackId];
    },
    [],
  );

  const registerCanvasFormUpdateCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      canvasFormUpdateCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterCanvasFormUpdateCallback = useCallback(
    (callbackId: string) => {
      delete canvasFormUpdateCallbacksRef.current[callbackId];
    },
    [],
  );

  const registerCanvasComponentUpdatedCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      canvasComponentUpdatedCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterCanvasComponentUpdatedCallback = useCallback(
    (callbackId: string) => {
      delete canvasComponentUpdatedCallbacksRef.current[callbackId];
    },
    [],
  );

  const registerConfettiCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      confettiCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterConfettiCallback = useCallback((callbackId: string) => {
    delete confettiCallbacksRef.current[callbackId];
  }, []);

  // Methods for registering and unregistering sub-thread created callbacks
  const registerSubThreadCreatedCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      subThreadCreatedCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterSubThreadCreatedCallback = useCallback(
    (callbackId: string) => {
      delete subThreadCreatedCallbacksRef.current[callbackId];
    },
    [],
  );

  // Methods for registering and unregistering sub-thread update callbacks
  const registerSubThreadUpdateCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      subThreadUpdateCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterSubThreadUpdateCallback = useCallback(
    (callbackId: string) => {
      delete subThreadUpdateCallbacksRef.current[callbackId];
    },
    [],
  );

  // Methods for registering and unregistering message edited callbacks
  const registerMessageEditedCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      messageEditedCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterMessageEditedCallback = useCallback((callbackId: string) => {
    delete messageEditedCallbacksRef.current[callbackId];
  }, []);

  // Methods for registering and unregistering response artifact created callbacks
  const registerResponseArtifactCreatedCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      responseArtifactCreatedCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterResponseArtifactCreatedCallback = useCallback(
    (callbackId: string) => {
      delete responseArtifactCreatedCallbacksRef.current[callbackId];
    },
    [],
  );

  // Methods for registering and unregistering response artifact updated callbacks
  const registerResponseArtifactUpdatedCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      responseArtifactUpdatedCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterResponseArtifactUpdatedCallback = useCallback(
    (callbackId: string) => {
      delete responseArtifactUpdatedCallbacksRef.current[callbackId];
    },
    [],
  );

  // Methods for registering and unregistering individual agent start callbacks
  const registerIndividualAgentStartCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      individualAgentStartCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterIndividualAgentStartCallback = useCallback(
    (callbackId: string) => {
      delete individualAgentStartCallbacksRef.current[callbackId];
    },
    [],
  );

  // Methods for registering and unregistering individual agent finish callbacks
  const registerIndividualAgentFinishCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      individualAgentFinishCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterIndividualAgentFinishCallback = useCallback(
    (callbackId: string) => {
      delete individualAgentFinishCallbacksRef.current[callbackId];
    },
    [],
  );

  // Methods for registering and unregistering phone call started callbacks
  const registerPhoneCallStartedCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      phoneCallStartedCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterPhoneCallStartedCallback = useCallback(
    (callbackId: string) => {
      delete phoneCallStartedCallbacksRef.current[callbackId];
    },
    [],
  );

  // Methods for registering and unregistering phone call ended callbacks
  const registerPhoneCallEndedCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      phoneCallEndedCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterPhoneCallEndedCallback = useCallback((callbackId: string) => {
    delete phoneCallEndedCallbacksRef.current[callbackId];
  }, []);

  // Methods for registering and unregistering WebRTC call requested callbacks
  const registerWebRTCCallRequestedCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      webrtcCallRequestedCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterWebRTCCallRequestedCallback = useCallback(
    (callbackId: string) => {
      delete webrtcCallRequestedCallbacksRef.current[callbackId];
    },
    [],
  );

  // Methods for registering and unregistering WebRTC call dismissed callbacks
  const registerWebRTCCallDismissedCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      webrtcCallDismissedCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterWebRTCCallDismissedCallback = useCallback(
    (callbackId: string) => {
      delete webrtcCallDismissedCallbacksRef.current[callbackId];
    },
    [],
  );

  // Method to trigger call dismissed event
  const dismissWebRTCCall = useCallback(
    (threadId: string) => {
      handleWebRTCCallDismissed(threadId);
    },
    [handleWebRTCCallDismissed],
  );

  // Methods for registering and unregistering WebRTC call accepted callbacks
  const registerWebRTCCallAcceptedCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      webrtcCallAcceptedCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterWebRTCCallAcceptedCallback = useCallback(
    (callbackId: string) => {
      delete webrtcCallAcceptedCallbacksRef.current[callbackId];
    },
    [],
  );

  // Method to trigger call accepted event
  const acceptWebRTCCall = useCallback(
    (threadId: string) => {
      handleWebRTCCallAccepted(threadId);
    },
    [handleWebRTCCallAccepted],
  );

  // Active WebRTC call state management methods
  const setActiveWebRTCCall = useCallback(
    (
      threadId: string,
      callData: {
        agentName?: string;
        message?: string;
        structuredCallId?: string;
      },
    ) => {
      setActiveWebRTCCalls((prev) => ({
        ...prev,
        [threadId]: {
          ...callData,
          timestamp: Date.now(),
        },
      }));
    },
    [],
  );

  const clearActiveWebRTCCall = useCallback((threadId: string) => {
    setActiveWebRTCCalls((prev) => {
      const updated = { ...prev };
      delete updated[threadId];
      return updated;
    });
  }, []);

  const getActiveWebRTCCall = useCallback(
    (threadId: string) => {
      return activeWebRTCCalls[threadId] || null;
    },
    [activeWebRTCCalls],
  );

  // Methods for registering and unregistering token callbacks
  const registerTokenCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      tokenCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterTokenCallback = useCallback((callbackId: string) => {
    delete tokenCallbacksRef.current[callbackId];
  }, []);

  // Methods for registering and unregistering thinking token callbacks
  const registerThinkingTokenCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      thinkingTokenCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterThinkingTokenCallback = useCallback((callbackId: string) => {
    delete thinkingTokenCallbacksRef.current[callbackId];
  }, []);

  // Methods for registering and unregistering tool start callbacks
  const registerToolStartCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      toolStartCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterToolStartCallback = useCallback((callbackId: string) => {
    delete toolStartCallbacksRef.current[callbackId];
  }, []);

  // Methods for registering and unregistering tool end callbacks
  const registerToolEndCallback = useCallback(
    (callbackId: string, callback: EventCallback) => {
      toolEndCallbacksRef.current[callbackId] = callback;
    },
    [],
  );

  const unregisterToolEndCallback = useCallback((callbackId: string) => {
    delete toolEndCallbacksRef.current[callbackId];
  }, []);

  // State context value - only changes when state changes
  const stateValue = useMemo(() => {
    return {
      loadingThreads,
      abortingThreads,
      fileAgentActiveThreads,
      asyncTools,
    };
  }, [loadingThreads, abortingThreads, fileAgentActiveThreads, asyncTools]);

  // Actions context value - stable, only contains callbacks
  const actionsValue = useMemo(() => {
    return {
      setAsyncTools,
      setThreadLoading,
      setThreadAborting,
      setFileAgentActive,
      registerThread,
      unregisterThread,
      registerManagerBeginCallback,
      unregisterManagerBeginCallback,
      registerAgentStreamBeginCallback,
      unregisterAgentStreamBeginCallback,
      registerAgentStreamFinishCallback,
      unregisterAgentStreamFinishCallback,
      registerManagerCompleteCallback,
      unregisterManagerCompleteCallback,
      registerAgentErrorCallback,
      unregisterAgentErrorCallback,
      registerAbortSucceededCallback,
      unregisterAbortSucceededCallback,
      registerIndividualAgentStartCallback,
      unregisterIndividualAgentStartCallback,
      registerIndividualAgentFinishCallback,
      unregisterIndividualAgentFinishCallback,
      registerPhoneCallStartedCallback,
      unregisterPhoneCallStartedCallback,
      registerPhoneCallEndedCallback,
      unregisterPhoneCallEndedCallback,
      registerWebRTCCallRequestedCallback,
      unregisterWebRTCCallRequestedCallback,
      registerWebRTCCallDismissedCallback,
      unregisterWebRTCCallDismissedCallback,
      dismissWebRTCCall,
      registerWebRTCCallAcceptedCallback,
      unregisterWebRTCCallAcceptedCallback,
      acceptWebRTCCall,
      setActiveWebRTCCall,
      clearActiveWebRTCCall,
      getActiveWebRTCCall,
      registerTokenCallback,
      unregisterTokenCallback,
      registerThinkingTokenCallback,
      unregisterThinkingTokenCallback,
      registerToolStartCallback,
      unregisterToolStartCallback,
      registerToolEndCallback,
      unregisterToolEndCallback,
      registerToolIsAsyncCallback,
      unregisterToolIsAsyncCallback,
      registerAsyncToolStartCallback,
      unregisterAsyncToolStartCallback,
      registerAsyncToolProgressCallback,
      unregisterAsyncToolProgressCallback,
      registerAsyncToolEndCallback,
      unregisterAsyncToolEndCallback,
      registerAsyncToolErrorCallback,
      unregisterAsyncToolErrorCallback,
      registerAsyncToolAbortCallback,
      unregisterAsyncToolAbortCallback,
      registerWorkboxItemCreatedCallback,
      unregisterWorkboxItemCreatedCallback,
      registerCanvasAssetCreatedCallback,
      unregisterCanvasAssetCreatedCallback,
      registerCanvasAssetRemovedCallback,
      unregisterCanvasAssetRemovedCallback,
      registerCanvasFormUpdateCallback,
      unregisterCanvasFormUpdateCallback,
      registerCanvasComponentUpdatedCallback,
      unregisterCanvasComponentUpdatedCallback,
      registerConfettiCallback,
      unregisterConfettiCallback,
      registerSubThreadCreatedCallback,
      unregisterSubThreadCreatedCallback,
      registerSubThreadUpdateCallback,
      unregisterSubThreadUpdateCallback,
      registerMessageEditedCallback,
      unregisterMessageEditedCallback,
      registerResponseArtifactCreatedCallback,
      unregisterResponseArtifactCreatedCallback,
      registerResponseArtifactUpdatedCallback,
      unregisterResponseArtifactUpdatedCallback,
    };
  }, [
    setAsyncTools,
    setThreadLoading,
    setThreadAborting,
    setFileAgentActive,
    registerThread,
    unregisterThread,
    registerManagerBeginCallback,
    unregisterManagerBeginCallback,
    registerAgentStreamBeginCallback,
    unregisterAgentStreamBeginCallback,
    registerAgentStreamFinishCallback,
    unregisterAgentStreamFinishCallback,
    registerManagerCompleteCallback,
    unregisterManagerCompleteCallback,
    registerAgentErrorCallback,
    unregisterAgentErrorCallback,
    registerAbortSucceededCallback,
    unregisterAbortSucceededCallback,
    registerIndividualAgentStartCallback,
    unregisterIndividualAgentStartCallback,
    registerIndividualAgentFinishCallback,
    unregisterIndividualAgentFinishCallback,
    registerPhoneCallStartedCallback,
    unregisterPhoneCallStartedCallback,
    registerPhoneCallEndedCallback,
    unregisterPhoneCallEndedCallback,
    registerWebRTCCallRequestedCallback,
    unregisterWebRTCCallRequestedCallback,
    registerWebRTCCallDismissedCallback,
    unregisterWebRTCCallDismissedCallback,
    dismissWebRTCCall,
    registerWebRTCCallAcceptedCallback,
    unregisterWebRTCCallAcceptedCallback,
    acceptWebRTCCall,
    setActiveWebRTCCall,
    clearActiveWebRTCCall,
    getActiveWebRTCCall,
    registerTokenCallback,
    unregisterTokenCallback,
    registerToolStartCallback,
    unregisterToolStartCallback,
    registerToolEndCallback,
    unregisterToolEndCallback,
    registerToolIsAsyncCallback,
    unregisterToolIsAsyncCallback,
    registerAsyncToolStartCallback,
    unregisterAsyncToolStartCallback,
    registerAsyncToolProgressCallback,
    unregisterAsyncToolProgressCallback,
    registerAsyncToolEndCallback,
    unregisterAsyncToolEndCallback,
    registerAsyncToolErrorCallback,
    unregisterAsyncToolErrorCallback,
    registerAsyncToolAbortCallback,
    unregisterAsyncToolAbortCallback,
    registerWorkboxItemCreatedCallback,
    unregisterWorkboxItemCreatedCallback,
    registerCanvasAssetCreatedCallback,
    unregisterCanvasAssetCreatedCallback,
    registerCanvasAssetRemovedCallback,
    unregisterCanvasAssetRemovedCallback,
    registerCanvasFormUpdateCallback,
    unregisterCanvasFormUpdateCallback,
    registerCanvasComponentUpdatedCallback,
    unregisterCanvasComponentUpdatedCallback,
    registerConfettiCallback,
    unregisterConfettiCallback,
    registerSubThreadCreatedCallback,
    unregisterSubThreadCreatedCallback,
    registerSubThreadUpdateCallback,
    unregisterSubThreadUpdateCallback,
    registerMessageEditedCallback,
    unregisterMessageEditedCallback,
    registerResponseArtifactCreatedCallback,
    unregisterResponseArtifactCreatedCallback,
    registerResponseArtifactUpdatedCallback,
    unregisterResponseArtifactUpdatedCallback,
  ]);

  // Combined context value for backward compatibility
  const contextValue = useMemo(
    () => ({ ...stateValue, ...actionsValue }),
    [stateValue, actionsValue],
  );

  return (
    <ThreadActionsContext.Provider value={actionsValue}>
      <ThreadStateContext.Provider value={stateValue}>
        <ThreadLoadingContext.Provider value={contextValue}>
          {children}
        </ThreadLoadingContext.Provider>
      </ThreadStateContext.Provider>
    </ThreadActionsContext.Provider>
  );
};

// Hook for components that only need state (will re-render on state changes)
export const useThreadState = (): ThreadStateContextType => {
  const context = useContext(ThreadStateContext);

  if (context === undefined) {
    throw new Error(
      'useThreadState must be used within a ThreadLoadingProvider',
    );
  }

  return context;
};

// Hook for components that only need actions (stable, won't cause re-renders)
export const useThreadActions = (): ThreadActionsContextType => {
  const context = useContext(ThreadActionsContext);

  if (context === undefined) {
    throw new Error(
      'useThreadActions must be used within a ThreadLoadingProvider',
    );
  }

  return context;
};

// Legacy hook for backward compatibility - components using this will re-render on any state change
export const useThreadLoading = (): ThreadLoadingContextType => {
  const context = useContext(ThreadLoadingContext);

  if (context === undefined) {
    throw new Error(
      'useThreadLoading must be used within a ThreadLoadingProvider',
    );
  }

  return context;
};
